import { For, Show, createMemo, createSignal } from "solid-js";
import { specifications } from "@tailor-frame/patterns";
import { linkClick, routePath } from "../core/router";
import { choiceFor, defaultChoices, planFor, recipeById, recipes, settleChoices, stageLabels, unavailableReason, type PlanStep, type Recipe, type Stage } from "../core/workshop/recipes";
import { draftAdjustments, fittingAnswered, progressOf, projectPlan, nextStep, slotLocked, toileLocked, type FitCheck, type Project } from "../core/workshop/projects";
import { shelves, skillById, skillLevelLabels, skillLevels, skills, type SkillLevel } from "../core/workshop/skills";

const specificationFor = (patternId: string) => specifications.find((entry) => entry.specification.pattern.id === patternId)?.specification;
const patternName = (patternId: string) => specificationFor(patternId)?.pattern.name ?? patternId;
/** A pattern measurement's name, as the measurements page shows it. */
const measurementLabel = (patternId: string, id: string) => specificationFor(patternId)?.measurements[id]?.label ?? id;
const signedMm = (amount: number) => `${amount > 0 ? "+" : amount < 0 ? "−" : ""}${Math.abs(amount)} mm`;

/** The skills a recipe uses with these choices, each once, in the order they come up. */
function recipeSkills(recipe: Recipe, choices: Record<string, string>, toile: boolean) {
  return [...new Set(planFor(recipe, choices, { on: toile, rounds: 1 }).flatMap((step) => (step.skill ? [step.skill] : [])))];
}

const atLeast = (level: SkillLevel | undefined, wanted: SkillLevel) => skillLevels.indexOf(level ?? "new") >= skillLevels.indexOf(wanted);

function SkillBadge(props: { level: SkillLevel | undefined }) {
  return <span class={`skill-level level-${props.level ?? "new"}`}>{skillLevelLabels[props.level ?? "new"]}</span>;
}

function ProgressBar(props: { fraction: number }) {
  return <div class="workshop-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(props.fraction * 100)}><div style={{ width: `${Math.round(props.fraction * 100)}%` }} /></div>;
}

/** Your projects, newest first, and a way to start another. */
export function ProjectsPage(props: { projects: Project[] }) {
  const sorted = () => [...props.projects].sort((a, b) => b.updated - a.updated);
  return (
    <main class="page-content workshop-page">
      <div class="workshop-heading">
        <div><p class="eyebrow">WORKSHOP</p><h2>Your projects</h2></div>
        <a class="button primary-link" href={routePath({ name: "new-project" })} onClick={linkClick}>New project</a>
      </div>
      <Show when={sorted().length} fallback={
        <div class="workshop-empty"><h3>Start your first project</h3><p>Pick a pattern, choose its design and follow it step by step, from the draft and toile to the final press.</p><a class="button primary-link" href={routePath({ name: "new-project" })} onClick={linkClick}>New project</a></div>
      }>
        <ul class="workshop-cards">
          <For each={sorted()}>{(project) => {
            const recipe = recipeById(project.recipeId)!;
            const progress = () => progressOf(project);
            return (
              <li><a class="workshop-card" href={routePath({ name: "projects", projectId: project.id })} onClick={linkClick}>
                <span class="workshop-card-kicker">{patternName(recipe.patternId)}</span>
                <strong>{project.name}</strong>
                <small>{recipe.slots.map((slot) => choiceFor(slot, project.choices[slot.id])!.label).join(" · ")}</small>
                <ProgressBar fraction={progress().fraction} />
                <small>{progress().done} of {progress().total} steps{nextStep(project) ? ` · next: ${nextStep(project)!.title}` : " · finished"}</small>
              </a></li>
            );
          }}</For>
        </ul>
      </Show>
    </main>
  );
}

/** Starting a project: pick a recipe, then its design, for the body on the measurements page. */
export function NewProjectPage(props: { recipeId?: string; madeFor: string; skillProgress: Record<string, SkillLevel>; onCreate: (recipe: Recipe, choices: Record<string, string>, toile: boolean, name: string) => void }) {
  const recipe = () => recipeById(props.recipeId);
  return (
    <main class="page-content workshop-page">
      <Show when={recipe()} fallback={
        <>
          <div class="workshop-heading"><div><p class="eyebrow">NEW PROJECT · 1 OF 2</p><h2>Pick a pattern</h2></div></div>
          <p class="workshop-lead">Each pattern has a recipe: the skills it takes, in the order they are sewn. Drafting for <strong>{props.madeFor}</strong> · <a href={routePath({ name: "measurements" })} onClick={linkClick}>change measurements or size</a></p>
          <ul class="workshop-cards">
            <For each={recipes}>{(item) => {
              const needed = recipeSkills(item, defaultChoices(item), item.toile);
              const known = needed.filter((id) => atLeast(props.skillProgress[id], "practised")).length;
              return (
                <li><a class="workshop-card" href={routePath({ name: "new-project", recipeId: item.id })} onClick={linkClick}>
                  <span class="workshop-card-kicker">{item.slots.length} design choices</span>
                  <strong>{item.name}</strong>
                  <small>{item.summary}</small>
                  <small class="workshop-known">You’ve practised {known} of {needed.length} skills</small>
                </a></li>
              );
            }}</For>
          </ul>
        </>
      }>{(current) => <DesignForm recipe={current()} madeFor={props.madeFor} skillProgress={props.skillProgress} onCreate={props.onCreate} />}</Show>
    </main>
  );
}

function DesignForm(props: { recipe: Recipe; madeFor: string; skillProgress: Record<string, SkillLevel>; onCreate: (recipe: Recipe, choices: Record<string, string>, toile: boolean, name: string) => void }) {
  const [choices, setChoices] = createSignal(settleChoices(props.recipe, {}));
  const [toile, setToile] = createSignal(props.recipe.toile);
  const [name, setName] = createSignal(`${props.recipe.name} for ${props.madeFor}`);
  const needed = () => recipeSkills(props.recipe, choices(), toile());
  const choose = (slotId: string, choiceId: string) => setChoices(settleChoices(props.recipe, { ...choices(), [slotId]: choiceId }));
  return (
    <>
      <div class="workshop-heading"><div><p class="eyebrow">NEW PROJECT · 2 OF 2</p><h2>{props.recipe.name}: design</h2></div><a href={routePath({ name: "new-project" })} onClick={linkClick}>Pick another pattern</a></div>
      <div class="workshop-columns">
        <form class="workshop-form" onSubmit={(event) => { event.preventDefault(); props.onCreate(props.recipe, choices(), toile(), name().trim() || `${props.recipe.name} for ${props.madeFor}`); }}>
          <p class="workshop-lead">Drafted from {patternName(props.recipe.patternId)} for <strong>{props.madeFor}</strong> · <a href={routePath({ name: "measurements" })} onClick={linkClick}>change measurements or size</a></p>
          <For each={props.recipe.slots}>{(slot) => (
            <fieldset class="workshop-slot">
              <legend>{slot.label}</legend>
              <For each={slot.choices}>{(choice) => {
                const reason = () => unavailableReason(props.recipe, slot, choice, choices());
                return (
                  <label classList={{ unavailable: reason() !== undefined }}>
                    <input type="radio" name={slot.id} value={choice.id} checked={choices()[slot.id] === choice.id} disabled={reason() !== undefined} onChange={() => choose(slot.id, choice.id)} />
                    <span>{choice.label}</span>
                    <Show when={reason()}>{(text) => <small>{text()}</small>}</Show>
                  </label>
                );
              }}</For>
            </fieldset>
          )}</For>
          <fieldset class="workshop-slot">
            <legend>Toile</legend>
            <label><input type="checkbox" checked={toile()} onChange={(event) => setToile(event.currentTarget.checked)} /><span>Make a toile first and fit it</span><small>Calico, tacked on the fitting seams. Repeat until it fits.</small></label>
          </fieldset>
          <label class="workshop-name"><span>Project name</span><input value={name()} onInput={(event) => setName(event.currentTarget.value)} /></label>
          <button type="submit" class="primary">Start project</button>
        </form>
        <aside class="workshop-side">
          <p class="eyebrow">SKILLS THIS DESIGN USES</p>
          <ul class="workshop-skill-list">
            <For each={needed()}>{(id) => <li><span>{skillById(id)?.name ?? id}</span><SkillBadge level={props.skillProgress[id]} /></li>}</For>
          </ul>
        </aside>
      </div>
    </>
  );
}

interface ProjectPageProps {
  project?: Project;
  projectId: string;
  skillProgress: Record<string, SkillLevel>;
  onChange: (project: Project) => void;
  onDelete: (id: string) => void;
  onOpenDraft: (project: Project, view: "workspace" | "print") => void;
  onPractised: (skillId: string) => void;
}

/** One project: its design on the left, and every step from draft to final press, ticked off as they are done. */
export function ProjectPage(props: ProjectPageProps) {
  return (
    <main class="page-content workshop-page">
      <Show when={props.project} fallback={<div class="page-heading"><p class="eyebrow">NOT FOUND</p><h2>No project called “{props.projectId}”</h2><p><a href="/projects" onClick={linkClick}>Back to your projects</a></p></div>}>
        {(project) => <ProjectView {...props} project={project()} />}
      </Show>
    </main>
  );
}

function ProjectView(props: ProjectPageProps & { project: Project }) {
  const recipe = () => recipeById(props.project.recipeId)!;
  // Steps that have not changed keep their objects, so ticking one does not rebuild the list (and lose focus).
  const plan = createMemo<PlanStep[]>((previous) => projectPlan(props.project).map((step) => {
    const kept = previous?.find((item) => item.id === step.id);
    return kept && JSON.stringify(kept) === JSON.stringify(step) ? kept : step;
  }));
  const progress = () => progressOf(props.project);
  const next = () => nextStep(props.project);
  const stages = createMemo(() => (["draft", "toile", "cut", "make", "finish"] as Stage[]).filter((stage) => plan().some((step) => step.stage === stage)));
  const stageSteps = (stage: Stage) => plan().filter((step) => step.stage === stage);
  const update = (change: Partial<Project>) => props.onChange({ ...props.project, ...change, updated: Date.now() });
  const toggle = (step: PlanStep) => {
    const done = props.project.done.includes(step.id);
    update({ done: done ? props.project.done.filter((id) => id !== step.id) : [...props.project.done, step.id] });
  };
  const choose = (slotId: string, choiceId: string) => update({ choices: settleChoices(recipe(), { ...props.project.choices, [slotId]: choiceId }) });
  const lastRound = () => props.project.toileRounds.length;
  const adjustments = createMemo(() => draftAdjustments(props.project));
  /** The check's measurements this pattern drafts from. */
  const adjustable = (measurements: string[] | undefined) => (measurements ?? []).filter((id) => specificationFor(recipe().patternId)?.measurements[id] !== undefined);
  const setCheck = (round: number, check: string, value: FitCheck | undefined) => {
    const rounds = props.project.toileRounds.map((item, index) => {
      if (index !== round - 1) return item;
      const checks = { ...item.checks };
      if (value) checks[check] = value; else delete checks[check];
      return { checks };
    });
    update({ toileRounds: rounds });
  };
  const finishFitting = (again: boolean) => {
    const fitStep = `toile-${lastRound()}-fit`;
    const done = props.project.done.includes(fitStep) ? props.project.done : [...props.project.done, fitStep];
    update({ done, toileRounds: again ? [...props.project.toileRounds, { checks: {} }] : props.project.toileRounds });
  };
  return (
    <>
      <div class="workshop-heading">
        <div><p class="eyebrow">PROJECT · {patternName(recipe().patternId).toUpperCase()}</p><h2>{props.project.name}</h2></div>
        <a href={routePath({ name: "projects" })} onClick={linkClick}>All projects</a>
      </div>
      <div class="workshop-columns">
        <aside class="workshop-side">
          <p class="workshop-lead">Made for <strong>{props.project.madeFor}</strong></p>
          <ProgressBar fraction={progress().fraction} />
          <p class="workshop-muted">{progress().done} of {progress().total} steps done</p>
          <Show when={next()}>{(step) => <div class="workshop-next"><p class="eyebrow">UP NEXT</p><strong>{step().title}</strong><Show when={step().skill}>{(id) => <SkillBadge level={props.skillProgress[id()]} />}</Show></div>}</Show>
          <p class="eyebrow workshop-side-title">DESIGN</p>
          <For each={recipe().slots}>{(slot) => (
            <label class="workshop-select">
              <span>{slot.label}<Show when={slotLocked(props.project, slot.id)}><small> · started</small></Show></span>
              <select disabled={slotLocked(props.project, slot.id)} title={slotLocked(props.project, slot.id) ? "Untick its steps to change this design" : undefined} onChange={(event) => choose(slot.id, event.currentTarget.value)}>
                <For each={slot.choices}>{(choice) => {
                  const reason = () => unavailableReason(recipe(), slot, choice, props.project.choices);
                  return <option value={choice.id} selected={props.project.choices[slot.id] === choice.id} disabled={reason() !== undefined}>{choice.label}{reason() ? ` (${reason()!.toLowerCase()})` : ""}</option>;
                }}</For>
              </select>
            </label>
          )}</For>
          <Show when={Object.keys(adjustments()).length}>
            <p class="eyebrow workshop-side-title">TOILE CHANGES</p>
            <ul class="workshop-adjustments">
              <For each={Object.entries(adjustments())}>{([id, amount]) => <li><span>{measurementLabel(recipe().patternId, id)}</span><strong>{signedMm(amount)}</strong></li>}</For>
            </ul>
            <p class="workshop-muted">Added to {props.project.madeFor}’s measurements whenever this project’s draft opens.</p>
          </Show>
          <label class="workshop-check"><input type="checkbox" checked={props.project.toile} disabled={toileLocked(props.project)} onChange={(event) => update({ toile: event.currentTarget.checked })} /><span>Make a toile first</span></label>
          <button type="button" class="workshop-delete" onClick={() => { if (window.confirm(`Delete “${props.project.name}”? Its progress can’t be brought back.`)) props.onDelete(props.project.id); }}>Delete project</button>
        </aside>

        <section class="workshop-stages" aria-label="Steps">
          <For each={stages()}>{(stage) => (
            <div class="workshop-stage">
              <h3><span>{stageLabels[stage]}</span><small>{stageSteps(stage).filter((step) => props.project.done.includes(step.id)).length}/{stageSteps(stage).length}</small></h3>
              <ol>
                <For each={stageSteps(stage)}>{(step) => {
                  const skill = () => skillById(step.skill);
                  const done = () => props.project.done.includes(step.id);
                  return (
                    <li classList={{ done: done(), next: next()?.id === step.id }}>
                      <div class="workshop-step">
                        <label class="workshop-tick"><input type="checkbox" checked={done()} onChange={() => toggle(step)} /><span>{step.title}<Show when={step.count && step.count > 1}> ×{step.count}</Show></span></label>
                        <div class="workshop-step-tools">
                          <Show when={skill()}>{(item) => <><span class="workshop-skill-name">{item().name}</span><SkillBadge level={props.skillProgress[item().id]} /></>}</Show>
                          <Show when={step.action === "draft"}><button type="button" onClick={() => props.onOpenDraft(props.project, "workspace")}>Open draft</button></Show>
                          <Show when={step.action === "print"}><button type="button" onClick={() => props.onOpenDraft(props.project, "print")}>Print</button></Show>
                          <Show when={skill()?.guide}>{(guide) => <a class="button" href={routePath({ name: "pockets", pocketId: guide() })} onClick={linkClick}>Guide</a>}</Show>
                          <Show when={skill() && !done() && !atLeast(props.skillProgress[skill()!.id], "practised")}><button type="button" onClick={() => props.onPractised(skill()!.id)} title="Mark this skill as practised on scraps">Practised</button></Show>
                        </div>
                      </div>
                      <Show when={step.action === "fitting" && step.round}>{(round) => {
                        const toileRound = () => props.project.toileRounds[round() - 1] ?? { checks: {} };
                        return (
                          <div class="workshop-fitting">
                            <For each={recipe().fitChecks}>{(check) => {
                              const value = () => toileRound().checks[check.label];
                              const set = (next: FitCheck | undefined) => setCheck(round(), check.label, next);
                              return (
                                <div class="workshop-fit-row">
                                  <span>{check.label}</span>
                                  <div role="group" aria-label={check.label}>
                                    <button type="button" classList={{ active: value()?.state === "ok" }} aria-pressed={value()?.state === "ok"} onClick={() => set(value()?.state === "ok" ? undefined : { state: "ok" })}>Fits</button>
                                    <button type="button" classList={{ active: value()?.state === "change" }} aria-pressed={value()?.state === "change"} onClick={() => set(value()?.state === "change" ? undefined : { state: "change", note: value()?.note, adjust: value()?.adjust })}>Change</button>
                                  </div>
                                  <Show when={value()?.state === "change"}>
                                    <For each={adjustable(check.measurements)}>{(id) => (
                                      <label class="workshop-fit-adjust">
                                        <span>{measurementLabel(recipe().patternId, id)}</span>
                                        <input type="number" step="5" inputmode="numeric" value={value()?.adjust?.[id] ?? 0} onChange={(event) => {
                                          const amount = Math.round(Number(event.currentTarget.value) || 0);
                                          const adjust = { ...value()?.adjust, [id]: amount };
                                          if (amount === 0) delete adjust[id];
                                          set({ ...value()!, adjust });
                                        }} />
                                        <small>mm, + to let out or lengthen</small>
                                      </label>
                                    )}</For>
                                    <input class="workshop-fit-note" placeholder={adjustable(check.measurements).length ? "Note, such as where it pulls" : "What to change, such as straighten the side seam"} value={value()?.note ?? ""} onChange={(event) => set({ ...value()!, note: event.currentTarget.value })} />
                                  </Show>
                                </div>
                              );
                            }}</For>
                            <Show when={round() === lastRound() && !done()}>
                              <div class="workshop-fit-actions">
                                <Show when={fittingAnswered(recipe(), toileRound())} fallback={<small class="workshop-muted">Answer every check to finish this fitting.</small>}>
                                  <button type="button" class="primary" onClick={() => finishFitting(false)}>Toile fits: cut the cloth</button>
                                  <Show when={Object.values(toileRound().checks).some((item) => item.state === "change")}>
                                    <button type="button" onClick={() => finishFitting(true)}>Change the draft and make another toile</button>
                                  </Show>
                                </Show>
                              </div>
                            </Show>
                          </div>
                        );
                      }}</Show>
                    </li>
                  );
                }}</For>
              </ol>
            </div>
          )}</For>
        </section>
      </div>
    </>
  );
}

/** The skills library: every skill on its shelf, how far you have got with it, and its guide. */
export function SkillsPage(props: { skillProgress: Record<string, SkillLevel>; onPractised: (skillId: string) => void }) {
  const usedIn = (skillId: string) => recipes.filter((recipe) => recipe.tasks.some((task) => task.skill === skillId) || recipe.slots.some((slot) => slot.choices.some((choice) => choice.tasks?.some((task) => task.skill === skillId))) || (recipe.toile && ["toile", "tacking", "fitting"].includes(skillId)));
  const practised = () => skills.filter((skill) => atLeast(props.skillProgress[skill.id], "practised")).length;
  return (
    <main class="page-content workshop-page">
      <div class="workshop-heading">
        <div><p class="eyebrow">SKILLS LIBRARY</p><h2>Skills</h2></div>
        <p class="workshop-muted">{practised()} of {skills.length} practised · {skills.filter((skill) => skill.guide).length} with 3D guides</p>
      </div>
      <For each={shelves}>{(shelf) => (
        <section class="workshop-shelf">
          <h3>{shelf.label}</h3>
          <ul class="workshop-cards">
            <For each={skills.filter((skill) => skill.shelf === shelf.id)}>{(skill) => (
              <li class="workshop-card skill-card">
                <span class="workshop-card-kicker"><SkillBadge level={props.skillProgress[skill.id]} /></span>
                <strong>{skill.name}</strong>
                <small>{skill.summary}</small>
                <Show when={skill.requires?.length}><small class="workshop-muted">First: {skill.requires!.map((id) => skillById(id)?.name.toLowerCase() ?? id).join(", ")}</small></Show>
                <Show when={usedIn(skill.id).length}><small class="workshop-muted">Used in {usedIn(skill.id).map((recipe) => recipe.name.toLowerCase()).join(", ")}</small></Show>
                <div class="skill-card-tools">
                  <Show when={skill.guide} fallback={<small class="workshop-muted">No 3D guide yet</small>}>{(guide) => <a class="button" href={routePath({ name: "pockets", pocketId: guide() })} onClick={linkClick}>Watch the guide</a>}</Show>
                  <Show when={!atLeast(props.skillProgress[skill.id], "practised")}><button type="button" onClick={() => props.onPractised(skill.id)}>Mark practised</button></Show>
                </div>
              </li>
            )}</For>
          </ul>
        </section>
      )}</For>
    </main>
  );
}
